<template>
  <div>
    <b-modal
      id="upload-rw5-modal"
      ref="modal"
      v-model="showModal"
      title="رفع ملف ZIP يحتوي على ملفات RW5"
      size="lg"
      :ok-disabled="!selectedFile || uploading"
      ok-title="رفع"
      cancel-title="إلغاء"
      @hidden="resetForm"
      @ok="handleUpload"
    >
      <b-form-group label="اختر ملف ZIP">
        <b-form-file
          v-model="selectedFile"
          accept=".zip"
          placeholder="اختر ملف ZIP أو اسحبه هنا"
          drop-placeholder="اسحب الملف هنا"
          @change="onFileSelected"
        />
        <small class="text-muted">
          الحد الأقصى لحجم الملف: 100 ميجابايت
        </small>
      </b-form-group>

      <div
        v-if="selectedFile"
        class="mt-3"
      >
        <b-alert
          variant="info"
          show
        >
          <strong>الملف المحدد:</strong> {{ selectedFile.name }}<br>
          <strong>الحجم:</strong> {{ formatFileSize(selectedFile.size) }}
        </b-alert>
      </div>

      <b-spinner
        v-if="uploading"
        class="d-block text-center my-3"
      />
      <div
        v-if="uploading"
        class="text-center"
      >
        جاري رفع ومعالجة الملف...
      </div>

      <div
        v-if="uploadResult"
        class="mt-3"
      >
        <b-alert
          :variant="uploadResult.success ? 'success' : 'danger'"
          show
        >
          <h5>{{ uploadResult.success ? 'نجحت العملية' : 'فشلت العملية' }}</h5>
          <div v-if="uploadResult.data">
            <p><strong>إجمالي الملفات:</strong> {{ uploadResult.data.total_files }}</p>
            <p><strong>نجحت:</strong> {{ uploadResult.data.successful }}</p>
            <p v-if="uploadResult.data.errors && uploadResult.data.errors.length > 0">
              <strong>فشلت:</strong> {{ uploadResult.data.errors.length }}
            </p>
          </div>
        </b-alert>

        <div
          v-if="uploadResult.data && uploadResult.data.processed_files && uploadResult.data.processed_files.length > 0"
          class="mt-3"
        >
          <h6>الملفات المعالجة:</h6>
          <b-list-group>
            <b-list-group-item
              v-for="(file, index) in uploadResult.data.processed_files"
              :key="index"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{{ file.file_name }}</strong>
                <div
                  v-if="file.coordinates"
                  class="small text-muted"
                >
                  الإحداثيات: {{ file.coordinates.latitude.toFixed(7) }}, {{ file.coordinates.longitude.toFixed(7) }}
                </div>
              </div>
              <b-badge variant="success">
                نجح
              </b-badge>
            </b-list-group-item>
          </b-list-group>
        </div>

        <div
          v-if="uploadResult.data && uploadResult.data.errors && uploadResult.data.errors.length > 0"
          class="mt-3"
        >
          <h6>الأخطاء:</h6>
          <b-list-group>
            <b-list-group-item
              v-for="(error, index) in uploadResult.data.errors"
              :key="index"
              variant="danger"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{{ error.file }}</strong>
                <div class="small">
                  {{ error.error }}
                </div>
              </div>
              <b-badge variant="danger">
                فشل
              </b-badge>
            </b-list-group-item>
          </b-list-group>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="secondary"
          @click="cancel"
        >
          إلغاء
        </b-button>
        <b-button
          variant="primary"
          :disabled="!selectedFile || uploading"
          @click="handleUpload"
        >
          <b-spinner
            v-if="uploading"
            small
            class="mr-1"
          />
          {{ uploading ? 'جاري الرفع...' : 'رفع' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal,
  BFormGroup,
  BFormFile,
  BAlert,
  BSpinner,
  BListGroup,
  BListGroupItem,
  BBadge,
  BButton,
} from 'bootstrap-vue'
import geodesiaApi from '@/api/geodesia/geodesia'

export default {
  components: {
    BModal,
    BFormGroup,
    BFormFile,
    BAlert,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BBadge,
    BButton,
  },
  data() {
    return {
      showModal: false,
      selectedFile: null,
      uploading: false,
      uploadResult: null,
    }
  },
  methods: {
    show() {
      this.showModal = true
    },
    hide() {
      this.showModal = false
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file type
        if (!file.name.endsWith('.zip')) {
          this.$toast.error('يرجى اختيار ملف ZIP فقط')
          this.selectedFile = null
          return
        }

        // Validate file size (100MB = 100 * 1024 * 1024 bytes)
        const maxSize = 100 * 1024 * 1024
        if (file.size > maxSize) {
          this.$toast.error('حجم الملف كبير جداً. الحد الأقصى هو 100 ميجابايت')
          this.selectedFile = null
          return
        }

        this.selectedFile = file
        this.uploadResult = null
      }
    },
    async handleUpload() {
      if (!this.selectedFile) {
        this.$toast.error('يرجى اختيار ملف ZIP')
        return
      }

      this.uploading = true
      this.uploadResult = null

      try {
        const response = await geodesiaApi.uploadZipRw5Files(this.selectedFile)

        if (response.success) {
          this.uploadResult = response
          this.$toast.success(`تم معالجة ${response.data.successful} ملف بنجاح`)

          // Emit success event after a short delay to allow user to see results
          setTimeout(() => {
            this.$emit('uploaded', response)
            this.showModal = false
            this.resetForm()
          }, 2000)
        } else {
          this.uploadResult = {
            success: false,
            message: response.message || 'فشل رفع الملف',
          }
          this.$toast.error(response.message || 'فشل رفع الملف')
        }
      } catch (err) {
        console.error('Error uploading ZIP file:', err)
        const errorMessage = (err.response && err.response.data && err.response.data.message) || 'فشل رفع الملف'
        this.uploadResult = {
          success: false,
          message: errorMessage,
        }
        this.$toast.error(errorMessage)
      } finally {
        this.uploading = false
      }
    },
    resetForm() {
      this.selectedFile = null
      this.uploading = false
      this.uploadResult = null
      // Reset file input
      const fileInput = this.$el.querySelector('input[type="file"]')
      if (fileInput) {
        fileInput.value = ''
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`
    },
  },
}
</script>

<style scoped>
</style>
